import { uniqWith } from 'es-toolkit';
import { BufferGeometry, Float32BufferAttribute, ShapeUtils } from 'three';

import { SolidData } from '@paragon/contract-library';
import {
  findConvexHull,
  IsometricEmbedding,
  Point3D,
  Polygon2D,
  tryGetNormalDirection,
} from '@paragon/geometry-library';
import { notNullish } from '@paragon/utilities';

export function calculateTriangulatedMeshIndicesByFace(data: SolidData) {
  return data.faces
    .map((face) => {
      const vertices = face.outer.flatMap((vertexIndex) => Point3D.fromObject(data.vertices[vertexIndex]));
      const normalDirection = tryGetNormalDirection(vertices);
      if (!normalDirection) return null;
      const embedding = IsometricEmbedding.fromPointAndNormalDirection(vertices[0], normalDirection);
      const verticesMappedTo2D = vertices.map((vertex) => embedding.pointTo2D(vertex));

      const holes = face.inner.map((innerFace) =>
        innerFace.map((vertexIndex) => embedding.pointTo2D(Point3D.fromObject(data.vertices[vertexIndex]))),
      );

      // Gets the vertex indices relative to `face`.
      const triangulatedFaces = ShapeUtils.triangulateShape(verticesMappedTo2D, holes);

      const allVertexIndices = [...face.outer, ...face.inner.flat()];

      // Maps the `face` index to the `data.vertices` index.
      return triangulatedFaces.flatMap((faceVertexIndices) =>
        faceVertexIndices.map((faceVertexIndex) => allVertexIndices[faceVertexIndex]),
      );
    })
    .filter(notNullish);
}

export function calculationVerticesAndIndicesForBufferGeometry(data: SolidData) {
  const triangulatedMeshIndicesByFace = calculateTriangulatedMeshIndicesByFace(data);
  const positions = data.vertices.flatMap((point) => [point.x, point.z, -point.y]);
  return {
    positions: new Float32Array(positions),
    indices: new Uint32Array(triangulatedMeshIndicesByFace.flat()),
  };
}

export function calculateBufferGeometryFromSolidData(data: SolidData): BufferGeometry {
  const triangulatedMeshIndicesByFace = calculateTriangulatedMeshIndicesByFace(data);

  const positions = data.vertices.flatMap((point) => [point.x, point.z, -point.y]);

  const geometry = new BufferGeometry()
    .setAttribute('position', new Float32BufferAttribute(positions, 3))
    .setIndex(triangulatedMeshIndicesByFace.flatMap((face) => face));

  // These groups are depended on by `RoofSolidVisual` in order to have separate materials per face.
  let start = 0;
  for (let i = 0; i < triangulatedMeshIndicesByFace.length; i++) {
    const count = triangulatedMeshIndicesByFace[i].length;
    geometry.addGroup(start, count, i);
    start += count;
  }

  geometry.computeVertexNormals();
  geometry.computeBoundingBox();

  return geometry;
}

export function projectSolidDataToPolygon2D(data: SolidData): Polygon2D {
  const vertices = data.vertices.map(Point3D.fromObject).map((v) => v.projectTo2D());
  const uniqueVertices = uniqWith(vertices, (a, b) => a.equals(b));

  return new Polygon2D(findConvexHull(uniqueVertices));
}
